import type { ReactNode } from 'react'
import type { Review } from '../../data'
import { useWidget } from '../../../../providers/widget'
import { useFetchWidget } from '../../../../hooks'
import { propertyLinkRel } from '../../../../common/property-href'
import { usePropertyHrefFeed } from '../../../../common/use-property-href-feed'
import { reviewPropertyLink } from '../../helpers/utils'

/**
 * Wraps its children in a link to the review's property page (#1692, toolkit
 * review-showcase `link="property"`). Mounted only when `linkToProperty` is
 * on, so the default render keeps its exact markup and never subscribes to
 * the widget query. The route comes from the feed record the runtime already
 * holds (`['widget', feedId, widgetId]`: seeded by the renderer on SSR and by
 * the CSR ConfigLoader), so this adds no request — the same resolution the
 * explorer card uses. No slug → the children render unchanged: a plain name,
 * never a dead `#` link. A real page load on click (WP parity), no SPA routing.
 */
export function ReviewPropertyLink({
  review,
  className,
  children,
}: {
  review: Pick<Review, 'property_slug'>
  className: string
  children: ReactNode
}) {
  const { feedId, widgetId } = useWidget()
  const { data: widgetData } = useFetchWidget(feedId, widgetId)
  const pdpRoute = widgetData?.feed?.additional_info?.reverseProxy?.routes?.pdp || '/property/[slug]'
  const hrefFeed = usePropertyHrefFeed(widgetData?.feed)
  const link = reviewPropertyLink(pdpRoute, review, hrefFeed)
  if (!link) return <>{children}</>
  return (
    <a className={className} href={link.href} rel={propertyLinkRel(link)}>
      {children}
    </a>
  )
}
